import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import { formatDistance } from 'date-fns'
import React from 'react'
import { Link as RouterLink } from 'react-router'

import { usePersonChatSummary } from './personChatSummary'
import { logger } from '~/shared/lib/logger'
import { NAVY } from '~/shared/lib/theme'

interface PersonChatSummaryCardProps {
  personId: number
  onClose: () => void
}

const SKELETON_LINES = 4

/**
 * The agent-backed chat summary, shown in the communication thread popover.
 */
const PersonChatSummaryCard: React.FC<PersonChatSummaryCardProps> = ({ personId, onClose }) => {
  const { data: summary, isLoading, error, refetch } = usePersonChatSummary(personId)

  // A job that failed comes back as a normal 200 carrying `status: 'failed'`, so a query
  // error is not the only failure the card has to show. Without this a failed job would
  // render the same copy as "this member has too little history", under a title reading
  // as though everything worked.
  const jobFailed = summary?.status === 'failed'
  // `error` is `unknown` from the query wrapper, so it is narrowed to a boolean before
  // reaching JSX, where `unknown` is not a valid child.
  const hasError = error != null || jobFailed

  React.useEffect(() => {
    if (error) {
      logger.error(`Failed to fetch person chat summary personId: ${personId}`, { error })
    } else if (jobFailed) {
      logger.error(`Person chat summary job failed personId: ${personId}`, {
        summaryId: summary?.id,
      })
    }
  }, [error, jobFailed, personId, summary?.id])

  const title = () => {
    if (isLoading) {
      return 'Preparing Summary...'
    }
    if (hasError) {
      return 'No Summary Available'
    }
    return 'Chat Catch Up'
  }

  return (
    <Card sx={{ height: '100%' }} data-testid="person-chat-summary-card">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
              {(isLoading || hasError) && (
                <AutoFixHighIcon fontSize="small" sx={{ fill: NAVY[500] }} />
              )}
              <Typography variant="h6" sx={{ color: NAVY[500] }}>
                {title()}
              </Typography>
            </Box>
          }
          sx={{ paddingBottom: 0 }}
        />
        <IconButton onClick={onClose} aria-label="Close chat summary">
          <CloseIcon />
        </IconButton>
      </Box>
      <CardContent>
        {isLoading ? (
          Array.from({ length: SKELETON_LINES }, (_, index) => (
            <Skeleton key={index} variant="text" height={20} />
          ))
        ) : hasError ? (
          <Alert severity="warning">Temporary service issue. Please try again.</Alert>
        ) : summary?.content ? (
          <>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
              {summary.content}
            </Typography>
            {summary.cases.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Cases in this summary
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {summary.cases.map(linkedCase => (
                    <Chip
                      key={linkedCase.id}
                      size="small"
                      label={linkedCase.label}
                      component={RouterLink}
                      to={`/cases/${linkedCase.id}`}
                      clickable
                      data-testid="chat-summary-case-chip"
                    />
                  ))}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Tooltip title="Refresh summary">
                <IconButton
                  onClick={() => void refetch()}
                  size="small"
                  aria-label="Refresh summary"
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Updated {formatDistance(new Date(summary.created_at), new Date())} ago
              </Typography>
            </Box>
          </>
        ) : summary?.status === 'complete' || summary?.status === 'skipped' ? (
          // The job finished with nothing to say: the member has too little chat history.
          <Alert severity="info">Not enough chat history to generate a summary yet.</Alert>
        ) : (
          <Alert severity="info">No summary available for this patient.</Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default PersonChatSummaryCard
